import React, { useCallback, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { injectIntl } from 'react-intl';
import Button from 'components/Shared/Common/Button';
import Logger from 'services/debug/logger';

import useMediaDevicesPermissions from 'components/Shared/Call/useMediaDevicesPermissions';
import useWhyDidYouUpdate from 'hooks/useWhyDidYouUpdate';
import { useSelector } from 'react-redux';
import { getAvailableDevicesByKind } from 'selectors/roomSettings';

const logger = new Logger('component:room:DeviceControlButton');

const checkMessage = ([condition, value]) => condition ? value : null;

const getMessage = (rules = []) => rules.map(checkMessage).find(m => !!m);

const DeviceControlButton = (props) => {
    const {
        isDisabled,
        isChecked,
        onToggle,
        intl,

        type,
        mediaType,
        deviceType,
        btnOffLabel,
        btnOnLabel,
        formatter,
        errorId,
    } = props;
    logger.setName(`component:room:controls:button [${type}]`);
    const devices = useSelector(state => getAvailableDevicesByKind(state, mediaType));
    const permissions = useMediaDevicesPermissions(deviceType);
    const hasDevice = devices && !!devices.length;
    const [, setState] = useState();
    const timeout = useRef();

    const i18n = formatter(intl)({ isOn: isChecked });

    useWhyDidYouUpdate(`component:room:controls:button:[${type}]`, { ...props, timeout: timeout?.current, permissions, devices, hasDevice });
    const handleTimeout = useCallback(() =>{
        timeout.current = undefined;
        setState({});
    }, []);

    useEffect(() => {
        if (timeout?.current) {
            clearTimeout(timeout.current);
            timeout.current = undefined;
            setState({});
        }
    }, [isChecked]);

    useEffect(() => () => {
        if (timeout?.current) {
            clearTimeout(timeout.current);
            timeout.current = undefined;
        }
    }, []);
    
    const handleClick = useCallback(() => {
        timeout.current = setTimeout(handleTimeout, 3000);

        const checked= !isChecked;
        if (!isDisabled) {
            if (checked && hasDevice !== false && permissions !== 'deny') {
                logger.log('handleToggle', type, true);
                onToggle(true);
            } else if (!checked) {
                logger.log('handleToggle', type, false);
                onToggle(false);
            } else {
                logger.warn('handleToggle:skip', type, checked, hasDevice, permissions);
            }
        } else {
            logger.warn('handleToggle:skip', type, checked, isDisabled, hasDevice, permissions);
        }
        setState({});
    }, [isDisabled, hasDevice, permissions, onToggle, isChecked, handleTimeout]);

    const error = getMessage([
        [errorId, errorId && intl.formatMessage({ id: errorId })],
        [hasDevice === false, i18n.noDevice],
        [permissions === 'deny', i18n.denied],
    ]);

    let color = isChecked ? 'lighten' : 'light-transparency';
    if (error && error !== i18n.button) {
        color = 'danger';
    }

    if (timeout?.current) {
        return (
            <Button
                isLoading
                tooltipPosition="top"
                className="loading"
                color="light-transparency"
                isDisabled
                tooltipText={intl.formatMessage({ id: "global.loading" })}
            />
        )
    }

    return (
        <Button
            iconName={isChecked ? btnOnLabel : btnOffLabel}
            tooltipPosition="top"
            color={color}
            isDisabled={isDisabled || hasDevice === false || permissions === 'deny'}
            tooltipText={error}
            onClick={handleClick}
        />
    );
}

DeviceControlButton.propTypes = {
    isDisabled: PropTypes.bool,
    isChecked: PropTypes.bool,
    onToggle: PropTypes.func.isRequired,
    intl: PropTypes.any.isRequired,
    type: PropTypes.string.isRequired,
    mediaType: PropTypes.string,
    deviceType: PropTypes.string,
    btnOffLabel: PropTypes.string.isRequired,
    btnOnLabel: PropTypes.string.isRequired,
    formatter: PropTypes.func.isRequired,
    errorId: PropTypes.string,
};

DeviceControlButton.defaultProps = {
    isDisabled: false,
    isChecked: false,
    mediaType: undefined,
    deviceType: undefined,
    errorId: undefined,
};

export default injectIntl((DeviceControlButton));
